import { useEffect, useRef } from "react";

// Intervalo por defecto del polling de las vistas de Postulaciones (45 s).
const DEFAULT_POLL_MS = 45000;

/**
 * Intervalo de polling (ms) para la tabla de Formularios y los modales de
 * Estadísticas. Se puede sobreescribir con `REACT_APP_STATS_POLL_MS`; si la
 * variable no está definida o es inválida, se usan 45 s.
 *
 * Nota: en Create React App las variables `REACT_APP_*` se leen en tiempo de
 * build, no en runtime.
 */
export const getStatsPollIntervalMs = (): number => {
  const raw = Number(process.env.REACT_APP_STATS_POLL_MS);
  return Number.isFinite(raw) && raw > 0 ? raw : DEFAULT_POLL_MS;
};

interface UsePollingOptions {
  /** Si es `false`, el timer no corre (útil para pausar sin desmontar). */
  enabled?: boolean;
  /** Intervalo en ms. Por defecto, `getStatsPollIntervalMs()`. */
  intervalMs?: number;
  /**
   * No dispara mientras la pestaña está en segundo plano; al volver al primer
   * plano refresca de inmediato. Evita golpear al servidor con pestañas ocultas.
   */
  pauseWhenHidden?: boolean;
}

/**
 * Ejecuta `callback` cada `intervalMs` mientras esté habilitado. Pensado para
 * refrescos "silenciosos" en segundo plano (sensación de tiempo real): el
 * callback decide cómo actualizar el estado sin spinners ni parpadeos.
 *
 * No dispara en el montaje: la carga inicial la hace el propio componente; este
 * hook solo agrega las recargas posteriores.
 */
export const usePolling = (
  callback: () => void,
  {
    enabled = true,
    intervalMs = getStatsPollIntervalMs(),
    pauseWhenHidden = true,
  }: UsePollingOptions = {},
): void => {
  // El callback se guarda en un ref para no reiniciar el timer en cada render
  // (el interval siempre invoca la última versión).
  const savedCallback = useRef(callback);
  useEffect(() => {
    savedCallback.current = callback;
  }, [callback]);

  useEffect(() => {
    if (!enabled || intervalMs <= 0) return;

    const run = () => savedCallback.current();

    const timer = window.setInterval(() => {
      if (pauseWhenHidden && document.visibilityState === "hidden") return;
      run();
    }, intervalMs);

    // Al volver la pestaña al primer plano, sincroniza de inmediato en vez de
    // esperar al siguiente tick.
    let onVisibilityChange: (() => void) | undefined;
    if (pauseWhenHidden) {
      onVisibilityChange = () => {
        if (document.visibilityState === "visible") run();
      };
      document.addEventListener("visibilitychange", onVisibilityChange);
    }

    return () => {
      window.clearInterval(timer);
      if (onVisibilityChange) {
        document.removeEventListener("visibilitychange", onVisibilityChange);
      }
    };
  }, [enabled, intervalMs, pauseWhenHidden]);
};
